// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, CustomerValidationDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@stoneage/Overrides/Components/AddressInfoDisplay";
import { mergeWithCss } from "@stoneage/XDL/Utilities";
import React, { useState } from "react";
import styled, { css } from "styled-components";

type BaseAddressModelWithValidation = BaseAddressModel & { validation?: CustomerValidationDto | null };

interface OwnProps {
    customer: BaseAddressModelWithValidation;
    isSelected?: boolean;
    onSelect: (customer: BaseAddressModel) => void;
    /** @deprecated Not used anymore */
    allowEditCustomer?: boolean;
    onEdit?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => void;
    extendedStyles?: CustomerCardStyles;
    isOverlay?: boolean;
}
const addressInfoTypographyStyle: TypographyProps = {
    size: 12,
    as: "p",
    css: css`
        letter-spacing: 0;
        line-height: 18px;
    `,
};
export interface CustomerCardStyles {
    container?: GridContainerProps;
    addressGridItem?: GridItemProps;
    address?: AddressInfoDisplayStyles;
    editGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    actionsWrapper?: InjectableCss;
    selectButton?: ButtonPresentationProps;
    editLink?: LinkPresentationProps;
}

export const customerCardStyles: CustomerCardStyles = {
    container: {
        css: css`
            padding: 1rem 0;
            border-bottom: 1px solid ${getColor("common.ui5")};
        `,
    },
    addressGridItem: { width: 6 },
    address: {
        customerNameText: {
            ...addressInfoTypographyStyle,
        },
        attentionText: {
            ...addressInfoTypographyStyle,
        },
        billingContactText: {
            as: "p",
            weight: "bold",
            size: 12,
            transform: "capitalize",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                margin: 8px 0;
            `,
        },
        companyNameText: {
            as: "p",
            size: 12,
            transform: "capitalize",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                margin-bottom: 8px;
            `,
        },
        customerNumberText: {
            as: "p",
            size: 12,
            transform: "capitalize",
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                margin-bottom: 8px;
            `,
        },
        address1Text: {
            ...addressInfoTypographyStyle,
        },
        address2Text: {
            ...addressInfoTypographyStyle,
        },
        address3Text: {
            ...addressInfoTypographyStyle,
        },
        address4Text: {
            ...addressInfoTypographyStyle,
        },
        cityStatePostalCodeText: {
            ...addressInfoTypographyStyle,
        },
        countryText: {
            ...addressInfoTypographyStyle,
        },
        phoneText: {
            ...mergeWithCss(addressInfoTypographyStyle, {
                css: css`
                    margin-top: 8px;
                `,
            }),
        },
        emailText: {
            ...mergeWithCss(addressInfoTypographyStyle, {
                css: css`
                    margin-top: 8px;
                `,
            }),
        },
    },
    actionsGridItem: {
        width: 6,
        css: css`
            justify-content: flex-end;
        `,
    },
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
        `,
    },
    selectButton: {
        variant: "primary",
        css: css`
            width: 155px;
        `,
    },
    editLink: {
        css: css`
            margin-top: 1rem;
        `,
    },
};

const ActionsWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

const CustomerCard = ({ customer, isSelected, onEdit, extendedStyles, onSelect, isOverlay }: OwnProps) => {
    const [styles] = useState(() => mergeToNew(customerCardStyles, extendedStyles));

    const handleEditClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => onEdit?.(event, customer);

    const hasEditableFields = customer.validation && Object.values(customer.validation).some(o => !o.isDisabled);
    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.addressGridItem}>
                <AddressInfoDisplay
                    {...customer}
                    state={customer.state?.abbreviation}
                    country={customer.country?.abbreviation}
                    extendedStyles={styles.address}
                />
            </GridItem>
            {isOverlay && hasEditableFields && onEdit && (
                <GridItem {...styles.editGridItem}>
                    <Link
                        {...styles.editLink}
                        onClick={handleEditClick}
                        data-test-selector={`customerCard_edit_${customer.id}`}
                    >
                        {translate("Edit")}
                    </Link>
                </GridItem>
            )}
            <GridItem {...styles.actionsGridItem}>
                <ActionsWrapper {...styles.actionsWrapper}>
                    <Button
                        {...styles.selectButton}
                        onClick={() => onSelect(customer)}
                        disabled={isSelected}
                        data-test-selector={`customerCard_select_${customer.id}`}
                        {...({ showIcon: false } as any)}
                    >
                        {isSelected ? "Selected Address" : "Select Address"}
                    </Button>
                    {!isOverlay && hasEditableFields && onEdit && (
                        <Link
                            {...styles.editLink}
                            onClick={handleEditClick}
                            data-test-selector={`customerCard_edit_${customer.id}`}
                        >
                            {translate("Edit")}
                        </Link>
                    )}
                </ActionsWrapper>
            </GridItem>
        </GridContainer>
    );
};

export default CustomerCard;
